import React from 'react';

interface SplitPanelTemplateProps {
  header: React.ReactNode;
  leftPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  leftPanelWidth?: 'w-1/4' | 'w-1/3' | 'w-2/5' | 'w-1/2';
  rightPanelWidth?: 'w-3/4' | 'w-2/3' | 'w-3/5' | 'w-1/2';
  leftPanelBg?: string;
  rightPanelBg?: string;
  className?: string;
  fullScreen?: boolean;
}

const panelWidthClasses = {
  'w-1/4': 'w-1/4',
  'w-1/3': 'w-1/3', 
  'w-2/5': 'w-2/5',
  'w-1/2': 'w-1/2',
  'w-3/5': 'w-3/5',
  'w-2/3': 'w-2/3',
  'w-3/4': 'w-3/4'
};

/**
 * SplitPanelTemplate - A reusable template for split-screen interfaces
 * 
 * This template provides a consistent structure for pages with:
 * - A fixed header section
 * - A flexible main content area with left and right panels
 * - Configurable panel widths and backgrounds
 * - Full-screen layout support
 * 
 * Used by: InvoiceEditPage, document viewers, comparison interfaces
 */
export const SplitPanelTemplate: React.FC<SplitPanelTemplateProps> = ({
  header,
  leftPanel,
  rightPanel,
  leftPanelWidth = 'w-1/3',
  rightPanelWidth = 'w-2/3',
  leftPanelBg = 'bg-white',
  rightPanelBg = 'bg-background-secondary',
  className = '',
  fullScreen = false,
}) => {
  const containerHeight = fullScreen ? 'h-screen' : 'min-h-screen';
  
  return (
    <div className={`${containerHeight} flex flex-col bg-background-secondary ${className}`}>
      {/* Header Section */}
      {header}

      {/* Main Content - Split Panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel */}
        <div className={`${panelWidthClasses[leftPanelWidth]} ${leftPanelBg} border-r border-secondary-200 overflow-y-auto`}>
          {leftPanel}
        </div>

        {/* Right Panel */}
        <div className={`${panelWidthClasses[rightPanelWidth]} ${rightPanelBg} overflow-hidden`}>
          {rightPanel}
        </div>
      </div>
    </div>
  );
};
